import type { KeyboardEvent, PointerEvent as ReactPointerEvent } from "react"
import { TModelNumberRow } from "./ModelNumbers"
import { ArrowIcon } from "components/ui/icons/quotations/ArrowIcon"
import { DetachIcon } from "components/ui/icons/detachIcon/DetachIcon"
import { GripIcon } from "components/ui/icons/grip/GripIcon"
import { ModelNumberOrderTip } from "components/ui/modelNumberOrderTip/ModelNumberOrderTip"
import type { ColumnsType } from "antd/es/table"
import style from "./ModelNumbers.module.scss"

const { toModelNumber, priorityHeader, priority, grip, gripSpacer, rank, newTag } = style

const isQuotationLocked = import.meta.env.VITE_LOCK_QUOTATION_FEATURE === "true"

type ModelNumbersColumnsParams = {
	modelNumberToId: Record<string, number>
	onDetach: (modelNumberId: number, modelNumberName: string) => void
	total: number
	isSaving: boolean
	onGripPointerDown: (event: ReactPointerEvent<HTMLButtonElement>, key: string) => void
	onGripKeyDown: (event: KeyboardEvent<HTMLButtonElement>, key: string) => void
	onGripBlur: (key: string) => void
}

export const ModelNumbersColumns = ({
	modelNumberToId,
	onDetach,
	total,
	isSaving,
	onGripPointerDown,
	onGripKeyDown,
	onGripBlur,
}: ModelNumbersColumnsParams) => {
	const cols: ColumnsType<TModelNumberRow> = [
		{
			title: (
				<span className={priorityHeader}>
					PRIORITY
					<ModelNumberOrderTip variant="list" />
				</span>
			),
			key: "priority",
			width: 110,
			render: (_, record) => (
				<span className={priority}>
					{total > 1 ? (
						<button
							type="button"
							className={grip}
							// aria-disabled, not disabled: a disabled button drops focus, stranding a keyboard user.
							aria-disabled={isSaving}
							aria-label={`Reorder ${record.modelNumber}, position ${record.position} of ${total}`}
							data-testid="model-number-reorder-grip"
							onPointerDown={(e) => onGripPointerDown(e, record.key)}
							onKeyDown={(e) => onGripKeyDown(e, record.key)}
							onBlur={() => onGripBlur(record.key)}
						>
							<GripIcon />
						</button>
					) : (
						// Keeps a lone row's rank in the same column as in a reorderable list.
						<span className={gripSpacer} />
					)}
					<span className={rank} data-testid="model-number-priority">
						{record.position}
					</span>
				</span>
			),
		},
		{
			title: "MODEL NUMBER",
			dataIndex: "modelNumber",
			key: "modelNumber",
			render: (_, record) => (
				<>
					{record.modelNumber}
					{record.isNew && <span className={newTag}>New</span>}
				</>
			),
		},
		{
			title: "WIDTH",
			dataIndex: "width",
			key: "width",
		},
		{
			title: "HEIGHT",
			dataIndex: "height",
			key: "height",
		},
		{
			title: "DEPTH",
			dataIndex: "depth",
			key: "depth",
		},
	]

	if (!isQuotationLocked) {
		cols.push({
			title: "",
			key: "goTo",
			render: (_, record) => {
				const id = modelNumberToId[record.modelNumber]
				const url = `/configuration/model-numbers?modelNumberID=${id}`
				return (
					<a href={url} target="_blank" rel="noopener noreferrer" className={toModelNumber}>
						<span>Go to model number</span>
						<ArrowIcon />
					</a>
				)
			},
		})
	}

	cols.push({
		title: "",
		key: "detach",
		render: (_, record) => {
			const id = modelNumberToId[record.modelNumber]
			return (
				<button onClick={() => onDetach(id, record.modelNumber)} disabled={isSaving}>
					<DetachIcon />
				</button>
			)
		},
	})

	return cols
}
